import httpClient from './httpClient';

// Helper function to parse MongoDB ObjectId format
const parseInventoryData = (inventory) => {
  if (!Array.isArray(inventory)) return inventory;
  
  return inventory.map(item => ({
    ...item,
    _id: item._id?.$oid || item._id || null,
    created_at: item.created_at?.$date || item.created_at,
    updated_at: item.updated_at?.$date || item.updated_at,
  }));
};

// Helper function to parse single product data
const parseProductData = (product) => {
  if (!product) return product;
  
  return {
    ...product,
    _id: product._id?.$oid || product._id || null,
    created_at: product.created_at?.$date || product.created_at,
    updated_at: product.updated_at?.$date || product.updated_at,
  };
};

const getInventory = async (page = 1, pageSize = 10, sortBy = 'name', sortOrder = 'asc', filters = {}, clientId = null, excludeZeroStock = false, inventoryType = null) => {
  const params = {
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_order: sortOrder,
    name: filters.name || undefined,
    category: filters.category || undefined,
    product_id: filters.product_id || undefined,
    product_code: filters.product_code || undefined,  // Separate filter for product_code
    part_number: filters.part_number || undefined,
    client_ref_number: filters.client_ref_number || undefined,
    low_stock: filters.stock_status === '库存不足' ? 'true' : undefined,
    // Manufacturing parameter range filters
    tip_diameter_min: filters.tip_diameter_range?.min ? parseFloat(filters.tip_diameter_range.min) : undefined,
    tip_diameter_max: filters.tip_diameter_range?.max ? parseFloat(filters.tip_diameter_range.max) : undefined,
    total_length_min: filters.total_length_range?.min ? parseFloat(filters.total_length_range.min) : undefined,
    total_length_max: filters.total_length_range?.max ? parseFloat(filters.total_length_range.max) : undefined,
    degree_min: filters.degree_range?.min ? parseFloat(filters.degree_range.min) : undefined,
    degree_max: filters.degree_range?.max ? parseFloat(filters.degree_range.max) : undefined,
    // Raw material exact dimension filters
    tip_diameter: filters.tip_diameter ? parseFloat(filters.tip_diameter) : undefined,
    total_length: filters.total_length ? parseFloat(filters.total_length) : undefined,
    // Include reservation details for reserved stock indicator
    include_reservations: filters.include_reservations ? 'true' : undefined,
    // Exclude zero stock items for cleaner inventory views
    exclude_zero_stock: excludeZeroStock ? 'true' : undefined,
  };

  // Add inventory_type filter for view mode switching
  if (inventoryType) {
    params.inventory_type = inventoryType;
  }

  // For global view, don't add client_id or use different endpoint
  // For client-specific view, add client_id parameter
  if (clientId && clientId !== 'global') {
    params.client_id = clientId;
  }

  // Always use the main inventory endpoint which supports both global and client-specific views
  const response = await httpClient.get('/api/inventory', { params });
  const data = response.data;

  // Parse inventory data - keep as 'inventory' array, don't transform to 'products'
  if (Array.isArray(data.inventory)) {
    data.inventory = parseInventoryData(data.inventory);
  } else {
    data.inventory = [];
  }

  // Use pagination from backend or construct it
  if (!data.pagination) {
    data.pagination = {
      page: data.page || page,
      page_size: data.page_size || pageSize,
      total_count: data.total_count || 0,
      total_pages: Math.ceil((data.total_count || 0) / (data.page_size || pageSize))
    };
  }

  return data;
};

const getLowStockItems = async () => {
  const response = await httpClient.get('/api/inventory/low-stock');
  const data = response.data;
  
  if (data.low_stock_items) {
    data.low_stock_items = parseInventoryData(JSON.parse(data.low_stock_items));
  }
  
  return data;
};

const getInventorySummary = async (clientId = null) => {
  const params = {};
  if (clientId && clientId !== 'global') {
    params.client_id = clientId;
  }
  const response = await httpClient.get('/api/inventory/summary', { params });
  return response.data;
};

const getInventoryHistory = async (productCode) => {
  const response = await httpClient.get(`/api/inventory/history/${productCode}`);
  const data = response.data;
  
  if (data.history) {
    data.history = JSON.parse(data.history);
  }
  
  return data;
};

const adjustInventory = async (adjustmentData) => {
  const response = await httpClient.post('/api/inventory/adjust', adjustmentData);
  const data = response.data;
  
  if (data.product) {
    data.product = JSON.parse(data.product);
  }
  
  return data;
};

// Advanced search with multiple criteria (using products collection with inventory focus)
const advancedSearch = async (searchCriteria, clientId = null) => {
  // Since inventory endpoints are limited, we'll leverage the products advanced search
  // but focus on inventory-related fields
  const response = await httpClient.post('/api/products/advanced-search', {
    ...searchCriteria,
    // Add inventory-specific context
    inventory_focus: true,
    client_id: clientId || undefined
  });
  const data = response.data;

  // Parse product data to handle MongoDB ObjectId format
  if (data.products) {
    data.products = parseInventoryData(data.products);
  }

  // Transform pagination format to match inventory endpoint structure
  if (data.total !== undefined) {
    data.pagination = {
      page: data.page || 1,
      page_size: data.pageSize || searchCriteria.page_size || 10,
      total_count: data.total,
      total_pages: data.totalPages || Math.ceil(data.total / (data.pageSize || searchCriteria.page_size || 10))
    };
  }

  return data;
};

// Get categories for inventory filtering (reuse from products)
const getCategories = async () => {
  const response = await httpClient.get('/api/products/categories');
  return response.data;
};

// Search inventory items
const searchInventory = async (query, page = 1, pageSize = 20) => {
  // Use products search but with inventory context
  const response = await httpClient.post('/api/products/search', {
    query,
    page,
    page_size: pageSize,
    inventory_context: true
  });
  const data = response.data;
  
  // Parse product data to handle MongoDB ObjectId format
  if (data.products) {
    data.products = parseInventoryData(data.products);
  }
  
  return data;
};

// Export inventory list functionality
const exportInventoryList = async (inventory, format = 'csv') => {
  // Client-side export functionality
  if (format === 'csv') {
    const headers = ['产品编号', '产品名称', '规格', '分类', '库存数量', '剩余数量', '位置'];
    const csvContent = [
      headers.join(','),
      ...inventory.map(item => [
        item.product_id || item.product_code || '',
        `"${item.name || ''}"`,
        `"${item.specification || ''}"`,
        `"${item.category || ''}"`,
        item.total_quantity || 0,
        item.remaining_quantity || 0,
        `"${item.location || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    return { success: true, format: 'csv' };
  }
  
  return { success: false, error: '不支持的格式' };
};

// Get inventory statistics
const getInventoryStats = async (clientId = null) => {
  try {
    const summary = await getInventorySummary(clientId);
    const lowStock = await getLowStockItems();
    const categories = await getCategories();

    return {
      // Core metrics from enhanced API
      total_items: summary.total_products || 0,
      total_remaining: summary.total_remaining || 0,
      total_reserved: summary.total_reserved || 0,
      total_value: summary.total_value || 0,

      // Stock health metrics
      low_stock_count: summary.low_stock_count || 0,
      out_of_stock_count: summary.out_of_stock_count || 0,
      overstock_count: summary.overstock_count || 0,
      healthy_stock_count: summary.healthy_stock_count || 0,

      // Category metrics
      category_count: summary.category_count || 0,

      // Recent activity metrics
      inbound_movements_30d: summary.inbound_movements_30d || 0,
      outbound_movements_30d: summary.outbound_movements_30d || 0,
      turnover_rate_30d: summary.turnover_rate_30d || 0,

      // Stock type totals for tile map
      finished_stock_total: summary.finished_stock_total || 0,
      raw_material_total: summary.raw_material_total || 0,
      leftover_total: summary.leftover_total || 0,

      // Context info
      is_global_view: summary.is_global_view !== false,
      client_id: summary.client_id || null,

      // Legacy data
      categories: categories.categories || [],
      low_stock_items: lowStock.low_stock_items || []
    };
  } catch (error) {
    console.error('Failed to load inventory stats:', error);
    return {
      total_items: 0,
      total_remaining: 0,
      total_reserved: 0,
      total_value: 0,
      low_stock_count: 0,
      out_of_stock_count: 0,
      overstock_count: 0,
      healthy_stock_count: 0,
      category_count: 0,
      inbound_movements_30d: 0,
      outbound_movements_30d: 0,
      turnover_rate_30d: 0,
      finished_stock_total: 0,
      raw_material_total: 0,
      leftover_total: 0,
      is_global_view: true,
      categories: [],
      low_stock_items: []
    };
  }
};

// Update single product quantity
const updateQuantity = async (productId, newQuantity, comments = '', location = '', clientId = null) => {
  try {
    const endpoint = clientId
      ? `/api/clients/${clientId}/inventory/${productId}`
      : '/api/inventory/update-quantity';

    const requestData = clientId
      ? {
          remaining_quantity: newQuantity,
          location: location || '',
          comments: comments || ''
        }
      : {
          product_id: productId,
          new_quantity: newQuantity,
          location: location || '',
          comments: comments || ''
        };

    const response = await httpClient.put(endpoint, requestData);
    const data = response.data;
    
    // Handle nested data structure - check if there's a nested data property
    const actualData = data.data || data;
    
    if (actualData.product) {
      try {
        const parsedProduct = typeof actualData.product === 'string' ? JSON.parse(actualData.product) : actualData.product;
        actualData.product = parseProductData(parsedProduct);
      } catch (parseError) {
        // If JSON parsing fails, throw an error
        throw new Error(`Failed to parse product data: ${parseError.message}`);
      }
    }
    
    return data;
  } catch (error) {
    if (error.message && error.message.includes('Failed to parse')) {
      throw error;
    }
    throw error;
  }
};

// Batch update multiple products
const batchUpdateQuantities = async (updates) => {
  try {
    const response = await httpClient.put('/api/inventory/batch-update', { updates });
    const data = response.data;
    
    // Handle nested data structure - check if there's a nested data property
    const actualData = data.data || data;
    
    if (actualData.updated_products) {
      try {
        let parsedProducts;
        
        if (Array.isArray(actualData.updated_products)) {
          parsedProducts = actualData.updated_products.map(product => {
            const parsed = typeof product === 'string' ? JSON.parse(product) : product;
            return parseProductData(parsed);
          });
        } else if (typeof actualData.updated_products === 'string') {
          const parsed = JSON.parse(actualData.updated_products);
          parsedProducts = Array.isArray(parsed) 
            ? parsed.map(product => parseProductData(product))
            : [parseProductData(parsed)];
        } else {
          parsedProducts = [parseProductData(actualData.updated_products)];
        }
        
        actualData.updated_products = parsedProducts;
      } catch (parseError) {
        throw new Error(`Failed to parse updated_products data: ${parseError.message}`);
      }
    }
    
    return data;
  } catch (error) {
    if (error.message && error.message.includes('Failed to parse')) {
      throw error;
    }
    throw error;
  }
};

// Add stock to existing product (increment quantity)
const addStock = async (productId, addQuantity, reason, supplierInfo = '', location = '', comments = '', clientId = null) => {
  try {
    const requestData = {
      product_id: productId,
      add_quantity: addQuantity,
      reason,
      supplier_info: supplierInfo || '',
      location: location || '',
      comments: comments || '',
      client_id: clientId || null
    };

    const response = await httpClient.post('/api/inventory/add-stock', requestData);
    const data = response.data;
    
    // Handle nested data structure - check if there's a nested data property
    const actualData = data.data || data;
    
    if (actualData.product) {
      try {
        const parsedProduct = typeof actualData.product === 'string' ? JSON.parse(actualData.product) : actualData.product;
        actualData.product = parseProductData(parsedProduct);
      } catch (parseError) {
        throw new Error(`Failed to parse product data: ${parseError.message}`);
      }
    }
    
    return data;
  } catch (error) {
    if (error.message && error.message.includes('Failed to parse')) {
      throw error;
    }
    throw error;
  }
};

/**
 * Delete inventory record at a specific location
 * @param {string} productId - Product ID
 * @param {string} clientId - Client ID
 * @param {string} location - Location to delete
 * @returns {Promise} Response from server
 */
const deleteInventoryRecord = async (productId, clientId, location) => {
  try {
    const response = await httpClient.delete('/api/inventory/location', {
      data: {
        product_id: productId,
        client_id: clientId,
        location: location
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting inventory record:', error);
    throw error;
  }
};

/**
 * Batch lookup inventory for multiple products
 * @param {string[]} productCodes - Array of product codes to look up
 * @param {object} options - Optional parameters
 * @param {string} options.clientId - Optional client ID for client-specific inventory (null or 'global' for global view)
 * @param {boolean} options.includeLocations - Whether to include per-location breakdown
 * @returns {Promise} Object with inventory data keyed by product code
 */
const batchLookupInventory = async (productCodes, options = {}) => {
  try {
    const { clientId = null, includeLocations = false } = options;
    const response = await httpClient.post('/api/inventory/batch-lookup', {
      product_codes: productCodes,
      client_id: clientId,
      include_locations: includeLocations
    });
    return response.data;
  } catch (error) {
    console.error('Error batch lookup inventory:', error);
    throw error;
  }
};

const getChartData = async (inventoryType, clientId = null, excludeZeroStock = false) => {
  const params = { inventory_type: inventoryType };
  if (clientId && clientId !== 'global') {
    params.client_id = clientId;
  }
  if (excludeZeroStock) {
    params.exclude_zero_stock = 'true';
  }
  const response = await httpClient.get('/api/inventory/chart-data', { params });
  return response.data;
};

const getStockByLocation = async (productIds, clientRef = null, includeAllClients = false) => {
  try {
    if (!productIds || productIds.length === 0) {
      return { stock_by_product: {} };
    }

    const params = {
      product_ids: productIds.join(',')
    };

    if (clientRef) {
      params.client_ref = clientRef;
    }

    if (includeAllClients) {
      params.include_all_clients = 'true';
    }

    const response = await httpClient.get('/api/inventory/stock-by-location', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching stock by location:', error);
    throw error;
  }
};

const inventoryService = {
  // Core inventory methods
  getInventory,
  getLowStockItems,
  getInventorySummary,
  getInventoryHistory,
  adjustInventory,
  getInventoryStats,
  batchLookupInventory,

  // Quantity update methods
  updateQuantity,
  batchUpdateQuantities,
  addStock,

  // Search methods
  advancedSearch,
  searchInventory,
  getCategories,

  // Utility methods
  exportInventoryList,
  deleteInventoryRecord,

  // Chart data
  getChartData,

  // Stock by location
  getStockByLocation,
};

export default inventoryService;
